/**
 * Billing — desktop dashboard. Rebuilt 1:1 against the design
 * folder's `billing.html` using iio.css classes:
 *   .projects-header (page title + studio name)
 *   .kpi-strip → 4 .kpi-tile (Active plan / Next charge / This month / Total payments)
 *   .responsive-grid-1-2 → Current plan card | Payment history card
 *   .card / .card-header / .card-title for both cards
 *   .proj-table for the payment history table
 *   .stage-pill + .stage-* for status pills
 *
 * Data flow unchanged from the previous Mantine version:
 *   - `useOrgSubscription` → plan card + KPI tiles
 *   - Live `onSnapshot` on `organizations/{orgId}/payments`
 *     (orderBy capturedAt desc, limit 25) → table + aggregations
 *   - `cancelRazorpaySubscription({ orgId, cancelAtCycleEnd: true })`
 *     → cancel modal action, only for studios still on legacy web
 *     (Razorpay) billing
 *
 * Plans are bought ONLY in the mobile app (App Store / Google Play).
 * This page shows the current plan, tells the owner where to upgrade
 * or manage it, and keeps legacy Razorpay payment history + invoices.
 *
 * Hardened vs the previous version:
 *   - Blind `as PaymentRow` cast replaced with `parsePaymentDoc`
 *     runtime guard (malformed docs skipped + warned, not rendered
 *     as NaN).
 *   - Inline hex colors replaced with `var(--*)` tokens.
 *   - Tier labels read from `lib/billingPlans.ts` (single source).
 *   - 6-month bar chart fitted inside the "This month" KPI tile
 *     per design (kept per user direction in plan file).
 */
import { Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { isSuperAdmin } from '@shared/perms';

import { PagePlaceholder } from '@/components/PagePlaceholder';
import { tierLabel } from '@/lib/billingPlans';
import { db, functions, httpsCallable } from '@/lib/firebase';
import {
  formatInr,
  formatInrCompact,
  paiseToInr,
  parsePaymentDoc,
  type PaymentRow,
} from '@/lib/paymentDocs';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useOrgSubscription } from '@/lib/useOrgSubscription';

// ───── Formatters ─────

function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtPeriod(period: string | undefined): string {
  if (period === 'annual') return 'Annual';
  if (period === 'monthly') return 'Monthly';
  return '—';
}

// ───── Status tone helpers ─────

type ActivePlanState = {
  /** Pill background + foreground tokens. */
  bg: string;
  fg: string;
  /** Big value color + optional border-top accent. */
  valueColor: string;
  borderTop: string | null;
  /** Subtitle line. */
  subLabel: string;
  /** Uppercase pill label. */
  pillLabel: string;
};

function activePlanState(
  status: string | undefined,
  tier: string | undefined,
): ActivePlanState {
  const isFree = tier === 'free' || !tier;
  if (isFree) {
    return {
      bg: 'var(--surface-muted, #F5F5F5)',
      fg: 'var(--text-secondary)',
      valueColor: 'var(--text-primary)',
      borderTop: null,
      subLabel: 'Free plan',
      pillLabel: 'FREE',
    };
  }
  switch (status) {
    case 'active':
      return {
        bg: 'var(--success-bg)',
        fg: 'var(--success-text)',
        valueColor: 'var(--text-primary)',
        borderTop: null,
        subLabel: 'Renewing',
        pillLabel: 'ACTIVE',
      };
    case 'pending':
      return {
        bg: 'var(--warning-bg)',
        fg: 'var(--warning-text)',
        valueColor: 'var(--warning)',
        borderTop: 'var(--warning)',
        subLabel: 'Awaiting first charge',
        pillLabel: 'PENDING',
      };
    case 'past_due':
    case 'halted':
      return {
        bg: 'var(--critical-bg)',
        fg: 'var(--critical-text)',
        valueColor: 'var(--critical)',
        borderTop: 'var(--critical)',
        subLabel: 'Payment failed',
        pillLabel: status.toUpperCase().replace('_', ' '),
      };
    case 'cancelled':
      return {
        bg: 'var(--critical-bg)',
        fg: 'var(--critical-text)',
        valueColor: 'var(--critical)',
        borderTop: 'var(--critical)',
        subLabel: 'Cancelled',
        pillLabel: 'CANCELLED',
      };
    case 'expired':
      return {
        bg: 'var(--surface-muted, #F5F5F5)',
        fg: 'var(--text-tertiary)',
        valueColor: 'var(--text-tertiary)',
        borderTop: null,
        subLabel: 'Expired',
        pillLabel: 'EXPIRED',
      };
    default:
      return {
        bg: 'var(--surface-muted, #F5F5F5)',
        fg: 'var(--text-secondary)',
        valueColor: 'var(--text-primary)',
        borderTop: null,
        subLabel: status ?? 'Unknown',
        pillLabel: (status ?? 'UNKNOWN').toUpperCase(),
      };
  }
}

type PaymentStatusVisual = {
  label: string;
  bg: string;
  fg: string;
};

function paymentStatusVisual(
  status: string,
  refunded: boolean,
): PaymentStatusVisual {
  if (refunded) {
    return {
      label: 'REFUNDED',
      bg: '#FFF7ED', // warning-ish for refund (matches design intent)
      fg: '#C2410C',
    };
  }
  if (status === 'captured') {
    return {
      label: 'CAPTURED',
      bg: 'var(--success-bg)',
      fg: 'var(--success-text)',
    };
  }
  if (status === 'failed') {
    return {
      label: 'FAILED',
      bg: 'var(--critical-bg)',
      fg: 'var(--critical-text)',
    };
  }
  if (status === 'authorized') {
    return {
      label: 'AUTHORIZED',
      bg: 'var(--warning-bg)',
      fg: 'var(--warning-text)',
    };
  }
  return {
    label: status.toUpperCase(),
    bg: '#F5F5F5',
    fg: '#525252',
  };
}

// ───── Bar chart (inline SVG, 6 months) ─────

function MonthBarChart({
  months,
}: {
  /** Oldest → newest, paise. Length 6. */
  months: { label: string; paise: number }[];
}) {
  const max = Math.max(1, ...months.map((m) => m.paise));
  const w = 100;
  const h = 24;
  const gap = 2;
  const barW = (w - gap * (months.length - 1)) / months.length;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width="100%"
      height={h}
      preserveAspectRatio="none"
      role="img"
      aria-label="Last 6 months of payments"
      style={{ display: 'block', marginTop: 8 }}
    >
      {months.map((m, i) => {
        const bh = Math.max(1, (m.paise / max) * h);
        const x = i * (barW + gap);
        const isCurrent = i === months.length - 1;
        return (
          <rect
            key={i}
            x={x}
            y={h - bh}
            width={barW}
            height={bh}
            fill="var(--text-primary)"
            opacity={isCurrent ? 1 : 0.25}
            rx={1}
          >
            <title>{`${m.label}: ${formatInr(paiseToInr(m.paise))}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

// ────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────

export function BillingPage() {
  const { org, loading, error } = useOrgSubscription();
  // Role gate — billing is the studio owner's responsibility
  // (matches the `ownerOnly: true` sidebar flag at App.tsx:150
  // and the firestore-rules billing-field blocklist). Without
  // this guard a Manager could URL-hop to /billing and see
  // payment history / plan tier / invoice URLs / a Cancel
  // button (writes deny server-side but the data view leaks).
  const { role: orgRole } = useCurrentOrgRole();
  const canManageBilling = isSuperAdmin(orgRole);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  useEffect(() => {
    if (!org?.id) return;
    setPaymentsLoading(true);
    setPaymentsError(null);
    return onSnapshot(
      query(
        collection(db, 'organizations', org.id, 'payments'),
        orderBy('capturedAt', 'desc'),
        limit(25),
      ),
      (snap) => {
        const parsed: PaymentRow[] = [];
        for (const d of snap.docs) {
          const row = parsePaymentDoc(d.id, d.data());
          if (row) parsed.push(row);
        }
        setPayments(parsed);
        setPaymentsLoading(false);
      },
      (err) => {
        setPaymentsError(err.message);
        setPaymentsLoading(false);
      },
    );
  }, [org?.id]);

  const onCancelConfirm = async () => {
    if (!org) return;
    setCancelling(true);
    try {
      const cancel = httpsCallable<
        { orgId: string; cancelAtCycleEnd: boolean },
        { ok: true }
      >(functions, 'cancelRazorpaySubscription');
      await cancel({ orgId: org.id, cancelAtCycleEnd: true });
      notifications.show({
        title: 'Subscription cancelled',
        message:
          "You'll keep your current plan benefits until the end of this billing period.",
        color: 'green',
        autoClose: 6000,
      });
      setCancelOpen(false);
    } catch (err) {
      const msg = (err as Error).message || 'Could not cancel subscription.';
      notifications.show({
        title: 'Cancellation failed',
        message: `${msg}\n\nEmail hello@interioros.app and we'll cancel manually.`,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setCancelling(false);
    }
  };

  // Aggregations (also feeds the 6-month bar chart).
  const stats = useMemo(() => {
    const now = Date.now();
    const today = new Date();
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const monthStartMs = monthStart.getTime();

    let thisMonthPaise = 0;
    let totalPaise = 0;
    let successfulCount = 0;

    const monthLabels: string[] = [];
    const monthPaise: number[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      monthLabels.push(d.toLocaleString('en-IN', { month: 'short' }));
      monthPaise.push(0);
    }
    const earliest = new Date(
      today.getFullYear(),
      today.getMonth() - 5,
      1,
    ).getTime();

    for (const p of payments) {
      const refunded = p.refund?.amountPaise ?? 0;
      const net = p.amountPaise - refunded;
      const ms = p.capturedAtMs ?? 0;
      if (p.status === 'captured') successfulCount += 1;
      if (ms >= monthStartMs && ms <= now) thisMonthPaise += net;
      totalPaise += net;
      if (ms >= earliest) {
        const d = new Date(ms);
        const idx =
          (d.getFullYear() - today.getFullYear()) * 12 +
          (d.getMonth() - today.getMonth()) +
          5;
        if (idx >= 0 && idx < 6) monthPaise[idx] += net;
      }
    }

    return {
      thisMonthInr: paiseToInr(thisMonthPaise),
      totalInr: paiseToInr(totalPaise),
      successfulCount,
      months: monthPaise.map((paise, i) => ({ label: monthLabels[i]!, paise })),
    };
  }, [payments]);

  // ── Loading / error / empty-org ─────────────────────────────
  if (loading || error || !org) {
    return (
      <div className="page active" id="page-billing" style={{ display: 'block' }}>
        <div className="projects-header">
          <div className="projects-title-row">
            <h1>Billing</h1>
          </div>
        </div>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>
            <i className="ph ph-receipt" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {error
              ? "Couldn't load billing"
              : loading
                ? 'Loading…'
                : 'No studio yet'}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {error
              ? error
              : !loading
                ? "You're signed in but haven't joined a studio. Open the mobile app to create or join one."
                : ''}
          </div>
        </div>
      </div>
    );
  }

  // ── Derived ────────────────────────────────────────────────
  const sub = org.subscription;
  const tier = sub?.tier ?? 'free';
  const status = sub?.status ?? 'active';
  const expiresMs = sub?.expiresAt?.toMillis() ?? null;
  const nextDays = expiresMs
    ? Math.max(0, Math.ceil((expiresMs - Date.now()) / 86400000))
    : null;
  const period = fmtPeriod(sub?.period ?? undefined);
  const plan = activePlanState(status, tier);
  const isFree = tier === 'free';
  const scheduledChangeTier = sub?.scheduledChange?.tier;
  const scheduledChangeMs = sub?.scheduledChange?.effectiveAt?.toMillis() ?? null;
  const isStoreBilled = sub?.source === 'webhook';
  const isLegacyWebSub =
    sub?.source === 'razorpay'
    && Boolean(sub?.razorpaySubscriptionId)
    && status === 'active'
    && sub?.willRenew !== false;

  // Role gate — render AFTER the loading guards so we don't
  // flash an access-denied screen during the initial role
  // resolution (orgRole is null while the org doc loads).
  if (!canManageBilling) {
    return (
      <div className="page active" id="page-billing" style={{ display: 'block' }}>
        <PagePlaceholder
          title="Billing"
          body="Billing and payments are managed by the studio owner (Super Admin) only. Ask your studio owner if changes are needed here."
        />
      </div>
    );
  }

  return (
    <div className="page active" id="page-billing" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Billing</h1>
          <div className="projects-stats">{org.name}</div>
        </div>
      </div>

      {/* ─── KPI Strip ─── */}
      <div className="kpi-strip">
        {/* Active plan */}
        <div
          className="kpi-tile"
          style={
            plan.borderTop
              ? { borderTop: `3px solid ${plan.borderTop}` }
              : undefined
          }
        >
          <div className="kpi-label">Active plan</div>
          <div className="kpi-value" style={{ color: plan.valueColor }}>
            {tierLabel(tier)}
          </div>
          <div className="kpi-sub">{plan.subLabel}</div>
        </div>

        {/* Next charge */}
        <div className="kpi-tile">
          <div className="kpi-label">Next charge</div>
          <div className="kpi-value">{isFree ? '—' : fmtDate(expiresMs)}</div>
          <div className="kpi-sub">
            {isFree
              ? 'No upcoming charge'
              : nextDays !== null
                ? `${nextDays}d remaining`
                : '—'}
          </div>
        </div>

        {/* This month — value + inline 6-month bar chart */}
        <div className="kpi-tile">
          <div className="kpi-label">This month</div>
          <div className="kpi-value">{formatInrCompact(stats.thisMonthInr)}</div>
          <div className="kpi-sub">Total spent</div>
          <MonthBarChart months={stats.months} />
        </div>

        {/* Total payments */}
        <div className="kpi-tile">
          <div className="kpi-label">Total Payments</div>
          <div className="kpi-value">{stats.successfulCount}</div>
          <div className="kpi-sub">Successful payments</div>
        </div>
      </div>

      {/* ─── Body: Current plan | Payment history ─── */}
      <div className="responsive-grid-1-2" style={{ marginTop: 24 }}>
        {/* Current plan card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Current plan</div>
          </div>
          <div
            style={{
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            <p
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                margin: 0,
              }}
            >
              {isFree
                ? 'Free plan — upgrade from the InteriorIO mobile app to unlock more projects and team members.'
                : isStoreBilled
                  ? 'Billed through the App Store / Google Play.'
                  : isLegacyWebSub
                    ? 'Auto-renews via UPI Autopay (web billing).'
                    : 'Managed by the InteriorIO team.'}
            </p>

            <Row label="Status">
              <span
                style={{
                  background: plan.bg,
                  color: plan.fg,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '4px 8px',
                  borderRadius: 4,
                }}
              >
                {plan.pillLabel}
              </span>
            </Row>

            <Row label="Tier">
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {tierLabel(tier)}
              </span>
            </Row>

            <Row label="Renews">
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {isFree ? '—' : fmtDate(expiresMs)}
              </span>
            </Row>

            <Row label="Period">
              <span style={{ fontSize: 14, fontWeight: 500 }}>{period}</span>
            </Row>

            {/* Inline status warnings */}
            {status === 'cancelled' && !isFree ? (
              <WarningBlock tone="critical">
                <i className="ph-bold ph-warning-circle" /> Cancellation
                scheduled. Access continues until {fmtDate(expiresMs)}.
              </WarningBlock>
            ) : null}
            {(status === 'past_due' || status === 'halted') && !isFree ? (
              <WarningBlock tone="critical">
                <i className="ph-bold ph-warning-circle" /> Payment failed.
                Update your method to keep access.
              </WarningBlock>
            ) : null}
            {scheduledChangeTier && scheduledChangeMs && !isFree ? (
              <WarningBlock tone="warning">
                <i className="ph-bold ph-clock" /> Plan change scheduled to{' '}
                <b>{tierLabel(scheduledChangeTier)}</b> on{' '}
                {fmtDate(scheduledChangeMs)}.
              </WarningBlock>
            ) : null}

            {/* Plans are bought / changed in the mobile app only. */}
            <WarningBlock tone="info">
              <i className="ph-bold ph-device-mobile" />{' '}
              {isStoreBilled
                ? 'To change or cancel your plan, open the InteriorIO app → Account → Plans → Manage subscription.'
                : 'To upgrade or change your plan, open the InteriorIO mobile app → Account → Plans.'}
            </WarningBlock>
            {isLegacyWebSub ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setCancelOpen(true)}
                  style={{ color: 'var(--critical)' }}
                >
                  Cancel web subscription
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Payment history card */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Payment history</div>
          </div>
          <div
            style={{
              padding: '16px 20px 0',
              fontSize: 13,
              color: 'var(--text-secondary)',
            }}
          >
            GST-compliant invoices for web (UPI Autopay) payments. App Store
            and Google Play receipts are in your store account.
          </div>
          <table className="proj-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th>Payment ID</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {paymentsLoading ? (
                <EmptyRow colSpan={6} text="Loading payments…" />
              ) : paymentsError ? (
                <EmptyRow
                  colSpan={6}
                  text={`Couldn't load payments: ${paymentsError}`}
                  tone="critical"
                />
              ) : payments.length === 0 ? (
                <EmptyRow
                  colSpan={6}
                  text="No web payments. App Store / Google Play receipts are in your store account."
                />
              ) : (
                payments.map((p) => {
                  const refunded = Boolean(p.refund);
                  const inr = paiseToInr(p.amountPaise);
                  const visual = paymentStatusVisual(p.status, refunded);
                  const paymentIdDisplay = p.paymentId.length > 18
                    ? `${p.paymentId.slice(0, 18)}…`
                    : p.paymentId;
                  return (
                    <tr key={p.id}>
                      <td>{fmtDate(p.capturedAtMs)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          InteriorIO {tierLabel(p.tier)} plan
                        </div>
                        {p.period ? (
                          <div style={{ fontSize: 12 }}>
                            {p.period} subscription
                          </div>
                        ) : null}
                        {refunded && p.refund ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--critical)',
                              marginTop: 2,
                            }}
                          >
                            Refunded {formatInr(paiseToInr(p.refund.amountPaise))}{' '}
                            on {fmtDate(p.refund.refundedAtMs)}
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="right tn"
                        style={{
                          fontWeight: 600,
                          textDecoration: refunded ? 'line-through' : 'none',
                          opacity: refunded ? 0.6 : 1,
                        }}
                      >
                        {formatInr(inr)}
                      </td>
                      <td>
                        <span
                          className="stage-pill"
                          style={{
                            background: visual.bg,
                            color: visual.fg,
                          }}
                        >
                          {visual.label}
                        </span>
                      </td>
                      <td
                        className="tn"
                        style={{ fontSize: 12, color: 'var(--text-secondary)' }}
                        title={p.paymentId}
                      >
                        {paymentIdDisplay}
                      </td>
                      <td>
                        {p.invoiceUrl ? (
                          <a
                            href={p.invoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary"
                            style={{
                              padding: '4px 8px',
                              fontSize: 12,
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <i className="ph ph-download-simple" />
                          </a>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── Help card ─── */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <div className="card-title">Need help with billing?</div>
        </div>
        <div
          style={{
            padding: 20,
            fontSize: 13,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          We respond within one business day.
          <br />
          Email <strong>hello@interioros.app</strong> or call{' '}
          <strong>+91 96662 11137</strong>. Our team can help with web
          invoices and GST formatting for your accountant. Refunds for App
          Store / Google Play purchases are handled by Apple / Google.
        </div>
      </div>

      {/* ─── Cancel modal ─── */}
      <Modal
        opened={cancelOpen}
        onClose={() => !cancelling && setCancelOpen(false)}
        title="Cancel auto-renewal?"
        size="md"
        centered
        styles={{
          content: { borderRadius: 12, border: '1px solid var(--border)' },
        }}
      >
        <Stack gap="md">
          <Text size="sm">
            Your <b>{tierLabel(tier)}</b> subscription will not renew at the
            end of this billing period.
          </Text>
          {expiresMs ? (
            <Text size="sm" c="dimmed">
              You'll keep full {tierLabel(tier)} plan benefits until{' '}
              <b>{fmtDate(expiresMs)}</b>, after which your studio will move
              back to the Free plan.
            </Text>
          ) : null}
          <Text size="sm" c="dimmed">
            You can subscribe again anytime from the InteriorIO mobile app.
          </Text>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8,
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Keep subscription
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={onCancelConfirm}
              disabled={cancelling}
              style={{
                background: 'var(--critical)',
                borderColor: 'var(--critical)',
              }}
            >
              {cancelling ? 'Cancelling…' : 'Yes, cancel renewal'}
            </button>
          </div>
        </Stack>
      </Modal>
    </div>
  );
}

// ───── Small render helpers ─────

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function WarningBlock({
  tone,
  children,
}: {
  tone: 'info' | 'warning' | 'critical';
  children: React.ReactNode;
}) {
  const bg =
    tone === 'info' ? 'var(--brand-bg)'
    : tone === 'warning' ? 'var(--warning-bg)'
    : 'var(--critical-bg)';
  const fg =
    tone === 'info' ? 'var(--brand)'
    : tone === 'warning' ? 'var(--warning-text)'
    : '#991B1B';
  return (
    <div
      style={{
        background: bg,
        color: fg,
        padding: 12,
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
      }}
    >
      {children}
    </div>
  );
}

function EmptyRow({
  colSpan,
  text,
  tone,
}: {
  colSpan: number;
  text: string;
  tone?: 'critical';
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{
          padding: '32px 16px',
          textAlign: 'center',
          color: tone === 'critical' ? 'var(--critical)' : 'var(--text-secondary)',
          fontSize: 13,
        }}
      >
        {text}
      </td>
    </tr>
  );
}
